import type { ApiResponse, DesignDoc, PricingOptionsInput } from "@scaffquote/shared";
import { redirect } from "react-router";
import type {
  AddStillResult,
  Bootstrap,
  ComputeResult,
  ConvertEnquiryInput,
  ConvertResult,
  CreatedTenant,
  Customer,
  CustomerInput,
  DesignWithDoc,
  Enquiry,
  EnquiryStatus,
  FreezeInput,
  FreezeResult,
  FrozenModel,
  Me,
  MediaItem,
  OutboxMessage,
  Quote,
  QuoteDetail,
  QuoteEvent,
  QuotePatch,
  Revision,
  RevisionLine,
  RevisionStill,
  RevokeLinkInput,
  RevokeLinkResult,
  SendInput,
  SendResult,
  ShareLink,
  Site,
  SiteInput,
  Task,
  TaskStatus,
  TenantBranding,
  WallTexture,
  WallTextureInput,
} from "./types.ts";

/**
 * The app's only way to the API. Same-origin fetch with the session cookie
 * (in development Vite proxies /api to wrangler dev), the ApiResponse
 * envelope unwrapped, and failures thrown as ApiError so loaders and
 * actions can show them. Given the loader's `request`, a 401 becomes a
 * redirect to sign-in that returns to the page afterwards.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Field issues from a VALIDATION failure: `[{ path: "email", message }]`. */
  get issues(): { path: string; message: string }[] {
    if (!Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is { path: string; message: string } =>
        typeof d === "object" &&
        d !== null &&
        typeof (d as { path?: unknown }).path === "string" &&
        typeof (d as { message?: unknown }).message === "string",
    );
  }

  /** The stored version from a VERSION_CONFLICT (409), if there is one. */
  get currentVersion(): number | null {
    const v = (this.details as { currentVersion?: unknown } | undefined)?.currentVersion;
    return typeof v === "number" ? v : null;
  }
}

export const NETWORK_ERROR_MESSAGE = "Can't reach the server. Check your connection and try again.";

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Query;
  /** Sent as JSON. */
  body?: unknown;
  signal?: AbortSignal;
  /** The loader or action request: a 401 then throws a redirect to sign-in that returns here. */
  request?: Request;
  /** Pin the tenant for this call (otherwise the session's active organisation). */
  tenantId?: string;
}

/** Path plus query string; empty, null and undefined values are left out. */
export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

/** Sign-in path that comes back to `next` (same-origin paths only). */
export function signInPath(next?: string | null): string {
  const safe = next?.startsWith("/") && !next.startsWith("//") ? next : null;
  return safe && safe !== "/" && !safe.startsWith("/sign-in")
    ? `/sign-in?next=${encodeURIComponent(safe)}`
    : "/sign-in";
}

/** Where a request was headed, as a path, for the sign-in redirect. */
function returnPath(request: Request): string {
  const url = new URL(request.url);
  return `${url.pathname}${url.search}`;
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.tenantId) headers["X-Tenant-Id"] = opts.tenantId;
  const init: RequestInit = {
    method: opts.method ?? (opts.body === undefined ? "GET" : "POST"),
    headers,
    credentials: "same-origin",
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  const signal = opts.signal ?? opts.request?.signal;
  if (signal) init.signal = signal;
  try {
    return await fetch(buildUrl(path, opts.query), init);
  } catch (error) {
    // An aborted navigation isn't a failure worth showing; let React Router see the abort.
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(0, "NETWORK", NETWORK_ERROR_MESSAGE);
  }
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // An HTML error page (proxy down, SPA fallback) rather than the API.
    throw new ApiError(res.status, "BAD_RESPONSE", "The server sent an unexpected reply.");
  }
}

function isEnvelope(value: unknown): value is ApiResponse<unknown> {
  return typeof value === "object" && value !== null && "success" in value;
}

/** Calls an /api route and returns `data`, or throws ApiError (or a sign-in redirect on 401). */
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await send(path, opts);
  if (res.status === 401 && opts.request) throw redirect(signInPath(returnPath(opts.request)));
  const json = await readJson(res);
  if (isEnvelope(json)) {
    if (json.success) return json.data as T;
    const { code, message, details } = json.error;
    throw new ApiError(res.status, code, message, details);
  }
  if (!res.ok) {
    throw new ApiError(res.status, `HTTP_${res.status}`, res.statusText || "Request failed");
  }
  throw new ApiError(res.status, "BAD_RESPONSE", "The server sent an unexpected reply.");
}

/**
 * Multipart uploads (wall textures, stills): the same envelope handling as
 * apiFetch, but the body is FormData and the browser sets its own
 * Content-Type with the boundary.
 */
export async function apiForm<T>(
  path: string,
  form: FormData,
  opts: Pick<RequestOptions, "method" | "signal" | "request" | "tenantId"> = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.tenantId) headers["X-Tenant-Id"] = opts.tenantId;
  const init: RequestInit = {
    method: opts.method ?? "POST",
    headers,
    body: form,
    credentials: "same-origin",
  };
  const signal = opts.signal ?? opts.request?.signal;
  if (signal) init.signal = signal;
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(0, "NETWORK", NETWORK_ERROR_MESSAGE);
  }
  if (res.status === 401 && opts.request) throw redirect(signInPath(returnPath(opts.request)));
  const json = await readJson(res);
  if (isEnvelope(json)) {
    if (json.success) return json.data as T;
    const { code, message, details } = json.error;
    throw new ApiError(res.status, code, message, details);
  }
  if (!res.ok) {
    throw new ApiError(res.status, `HTTP_${res.status}`, res.statusText || "Upload failed");
  }
  throw new ApiError(res.status, "BAD_RESPONSE", "The server sent an unexpected reply.");
}

/**
 * Better Auth routes (/api/auth/*) answer with their own JSON, not the
 * envelope: `{ code, message }` with a non-2xx status on failure.
 */
export async function authFetch<T>(path: string, body: unknown = {}): Promise<T> {
  const res = await send(`/api/auth${path}`, { method: "POST", body });
  const json = (await readJson(res)) as (T & { code?: string; message?: string }) | null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      json?.code ?? `HTTP_${res.status}`,
      json?.message ?? "Sign-in failed. Try again.",
    );
  }
  return json as T;
}

/** Options for typed helpers: the loader request (401 → sign-in) and an abort signal. */
export type CallOptions = Pick<RequestOptions, "request" | "signal" | "tenantId">;

interface ListOptions {
  q?: string;
  limit?: number;
  offset?: number;
}

const enc = encodeURIComponent;

export const api = {
  me: (o?: CallOptions) => apiFetch<Me>("/api/me", { ...o }),
  bootstrap: (o?: CallOptions) => apiFetch<Bootstrap>("/api/bootstrap", { ...o }),

  tenants: {
    create: (body: { name: string; slug: string }, o?: CallOptions) =>
      apiFetch<CreatedTenant>("/api/tenants", { ...o, method: "POST", body }),
  },

  /**
   * Branding is saved through a settings route the API doesn't have yet;
   * the settings screen explains that when this returns NOT_FOUND.
   */
  settings: {
    updateBranding: (body: { version: number; branding: TenantBranding }, o?: CallOptions) =>
      apiFetch<Bootstrap["tenant"]>("/api/settings/branding", { ...o, method: "PATCH", body }),
  },

  customers: {
    list: (q: ListOptions & { kind?: string } = {}, o?: CallOptions) =>
      apiFetch<Customer[]>("/api/customers", { ...o, query: { ...q } }),
    get: (id: string, o?: CallOptions) => apiFetch<Customer>(`/api/customers/${enc(id)}`, { ...o }),
    create: (body: CustomerInput, o?: CallOptions) =>
      apiFetch<Customer>("/api/customers", { ...o, method: "POST", body }),
    update: (id: string, body: Partial<CustomerInput> & { version: number }, o?: CallOptions) =>
      apiFetch<Customer>(`/api/customers/${enc(id)}`, { ...o, method: "PATCH", body }),
  },

  sites: {
    list: (q: ListOptions & { customerId?: string } = {}, o?: CallOptions) =>
      apiFetch<Site[]>("/api/sites", { ...o, query: { ...q } }),
    get: (id: string, o?: CallOptions) => apiFetch<Site>(`/api/sites/${enc(id)}`, { ...o }),
    create: (body: SiteInput, o?: CallOptions) =>
      apiFetch<Site>("/api/sites", { ...o, method: "POST", body }),
    update: (id: string, body: Partial<SiteInput> & { version: number }, o?: CallOptions) =>
      apiFetch<Site>(`/api/sites/${enc(id)}`, { ...o, method: "PATCH", body }),
  },

  enquiries: {
    list: (q: ListOptions & { status?: EnquiryStatus } = {}, o?: CallOptions) =>
      apiFetch<Enquiry[]>("/api/enquiries", { ...o, query: { ...q } }),
    get: (id: string, o?: CallOptions) => apiFetch<Enquiry>(`/api/enquiries/${enc(id)}`, { ...o }),
    convert: (id: string, body: ConvertEnquiryInput, o?: CallOptions) =>
      apiFetch<ConvertResult>(`/api/enquiries/${enc(id)}/convert`, {
        ...o,
        method: "POST",
        body,
      }),
  },

  quotes: {
    list: (q: ListOptions & { status?: string; customerId?: string } = {}, o?: CallOptions) =>
      apiFetch<Quote[]>("/api/quotes", { ...o, query: { ...q } }),
    get: (id: string, o?: CallOptions) => apiFetch<QuoteDetail>(`/api/quotes/${enc(id)}`, { ...o }),
    create: (
      body: {
        design: DesignDoc;
        title?: string | null;
        customerId?: string | null;
        siteId?: string | null;
      },
      o?: CallOptions,
    ) => apiFetch<{ quote: Quote }>("/api/quotes", { ...o, method: "POST", body }),
    update: (id: string, body: QuotePatch, o?: CallOptions) =>
      apiFetch<Quote>(`/api/quotes/${enc(id)}`, { ...o, method: "PATCH", body }),
    design: (id: string, o?: CallOptions) =>
      apiFetch<DesignWithDoc>(`/api/quotes/${enc(id)}/design`, { ...o }),
    saveDesign: (id: string, doc: DesignDoc, version: number, o?: CallOptions) =>
      apiFetch<DesignWithDoc>(`/api/quotes/${enc(id)}/design`, {
        ...o,
        method: "PUT",
        body: { doc, version },
      }),
    /** Server compute with the current pins; `doc` and `pricingOptions` preview unsaved changes. */
    compute: (
      id: string,
      body: {
        doc?: DesignDoc;
        pricingOptions?: PricingOptionsInput;
        clientTotals?: { subtotalCents?: number; totalIncGstCents?: number };
      },
      o?: CallOptions,
    ) => apiFetch<ComputeResult>(`/api/quotes/${enc(id)}/compute`, { ...o, method: "POST", body }),
    revisions: (id: string, o?: CallOptions) =>
      apiFetch<Revision[]>(`/api/quotes/${enc(id)}/revisions`, { ...o }),
    revision: (id: string, rev: number, o?: CallOptions) =>
      apiFetch<{ revision: Revision; lines: RevisionLine[] }>(
        `/api/quotes/${enc(id)}/revisions/${rev}`,
        { ...o },
      ),
    freeze: (id: string, body: FreezeInput, o?: CallOptions) =>
      apiFetch<FreezeResult>(`/api/quotes/${enc(id)}/revisions`, { ...o, method: "POST", body }),
    model: (id: string, rev: number, o?: CallOptions) =>
      apiFetch<FrozenModel>(`/api/quotes/${enc(id)}/revisions/${rev}/model`, { ...o }),
    send: (id: string, body: SendInput, o?: CallOptions) =>
      apiFetch<SendResult>(`/api/quotes/${enc(id)}/send`, { ...o, method: "POST", body }),
    events: (id: string, o?: CallOptions) =>
      apiFetch<QuoteEvent[]>(`/api/quotes/${enc(id)}/events`, { ...o, query: { limit: 200 } }),
    links: (id: string, o?: CallOptions) =>
      apiFetch<ShareLink[]>(`/api/quotes/${enc(id)}/links`, { ...o }),
    /** Stops a sent customer link working (view and accept). `linkId` picks one link. */
    revokeLink: (id: string, body: RevokeLinkInput, o?: CallOptions) =>
      apiFetch<RevokeLinkResult>(`/api/quotes/${enc(id)}/links/revoke`, {
        ...o,
        method: "POST",
        body,
      }),
    /** Photos from the quote's site visits, oldest first. */
    media: (id: string, o?: CallOptions) =>
      apiFetch<MediaItem[]>(`/api/quotes/${enc(id)}/media`, { ...o }),
    /** The walls' current photo textures (the live design; frozen copies come with the model). */
    walls: (id: string, o?: CallOptions) =>
      apiFetch<WallTexture[]>(`/api/quotes/${enc(id)}/walls`, { ...o }),
    /** Replaces a wall's photo texture. 409 VERSION_CONFLICT when someone else replaced it first. */
    putWall: (id: string, edgeId: string, input: WallTextureInput, o?: CallOptions) => {
      const form = new FormData();
      const ext = input.file.type === "image/jpeg" ? "jpg" : "webp";
      form.set("file", input.file, `${edgeId}.${ext}`);
      form.set("corners", JSON.stringify(input.corners));
      if (input.sourceMediaId) form.set("sourceMediaId", input.sourceMediaId);
      if (input.version !== undefined) form.set("version", String(input.version));
      return apiForm<WallTexture>(`/api/quotes/${enc(id)}/walls/${enc(edgeId)}`, form, {
        ...o,
        method: "PUT",
      });
    },
    /** Back to the generic material for that wall. */
    removeWall: (id: string, edgeId: string, version: number | null, o?: CallOptions) =>
      apiFetch<{ edgeId: string; removed: boolean; version: number }>(
        `/api/quotes/${enc(id)}/walls/${enc(edgeId)}`,
        { ...o, method: "DELETE", query: { version } },
      ),
    /** A revision's stills, in order: the first is the hero. `rev` is the number or the id. */
    stills: (id: string, rev: number | string, o?: CallOptions) =>
      apiFetch<RevisionStill[]>(`/api/quotes/${enc(id)}/revisions/${enc(String(rev))}/images`, {
        ...o,
      }),
    /** Adds (or, for the same view, replaces) a still on the current, unsent revision. */
    addStill: (id: string, rev: number | string, view: string, file: Blob, o?: CallOptions) => {
      const form = new FormData();
      form.set("view", view);
      form.set("file", file, `${view}.png`);
      return apiForm<AddStillResult>(
        `/api/quotes/${enc(id)}/revisions/${enc(String(rev))}/images`,
        form,
        { ...o },
      );
    },
  },

  tasks: {
    list: (
      q: {
        status?: TaskStatus;
        quoteId?: string;
        assignedTo?: string;
        due?: "now" | number;
        limit?: number;
      } = {},
      o?: CallOptions,
    ) => apiFetch<Task[]>("/api/tasks", { ...o, query: { ...q } }),
    complete: (id: string, version: number, o?: CallOptions) =>
      apiFetch<Task>(`/api/tasks/${enc(id)}/complete`, { ...o, method: "POST", body: { version } }),
  },

  public: {
    /** The frozen model behind a customer share link; the token is the credential. */
    model: (token: string, o?: CallOptions) =>
      apiFetch<FrozenModel>(`/api/public/q/${enc(token)}/model`, { ...o }),
  },

  dev: {
    /** Development only: the latest email the dev outbox caught for an address. */
    outbox: (to: string, o?: CallOptions) =>
      apiFetch<OutboxMessage[]>("/api/dev/outbox", { ...o, query: { to, limit: 1 } }),
  },
};

export const auth = {
  sendOtp: (email: string) =>
    authFetch<{ success: boolean }>("/email-otp/send-verification-otp", { email, type: "sign-in" }),
  verifyOtp: (email: string, otp: string) =>
    authFetch<{ token?: string; user?: { id: string } }>("/sign-in/email-otp", { email, otp }),
  signOut: () => authFetch<{ success: boolean }>("/sign-out", {}),
  setActiveOrganisation: (organizationId: string) =>
    authFetch<unknown>("/organization/set-active", { organizationId }),
  inviteMember: (email: string, role: string) =>
    authFetch<unknown>("/organization/invite-member", { email, role }),
};

/** Same-origin links to a frozen revision's files (opened in a new tab, cookie-authenticated). */
export const revisionFiles = {
  pdf: (quoteId: string, rev: number) => `/api/quotes/${enc(quoteId)}/revisions/${rev}/pdf`,
  print: (quoteId: string, rev: number) => `/api/quotes/${enc(quoteId)}/revisions/${rev}/print`,
};

/** The development outbox in a new tab, filtered to one address. */
export function devOutboxUrl(email: string): string {
  return buildUrl("/api/dev/outbox", { to: email, limit: 1 });
}
